import { motion } from 'framer-motion';
import {
  Activity,
  ArrowRight as ArrowRightIcon,
  Banknote as BanknotesIcon,
  BarChart3,
  Building2,
  ChartBar as ChartBarIcon,
  Code,
  Headphones,
  Megaphone,
  Presentation as PresentationChartLineIcon,
  TrendingUp,
  UserCheck,
  UserPlus,
  Users,
  Zap,
} from 'lucide-react';

import { useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';

import { Icon } from '@chiroup/components/Icon';
import { Icons } from '@chiroup/components/icons';
import Loading from '../common/Loading';
import analyticsService from './services/analytics.service';

const AnalyticsMain = () => {
  const navigate = useNavigate();

  const { data, isLoading, error } = useQuery(
    ['analytics-dashboards'],
    () => analyticsService.getDashboards(),
    {
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 minutes
    }
  );

  // Icon mapping for different dashboard types
  const getIconForDashboard = (dashboardName: string) => {
    const name = dashboardName.toLowerCase();
    if (name.includes('org')) return BarChart3;
    if (name.includes('sales')) return TrendingUp;
    if (name.includes('user') || name.includes('customer')) return Users;
    if (name.includes('activity') || name.includes('performance')) return Activity;

    return Building2;
  };

  // Category mapping for different dashboard types with specific names
  const getCategoryForDashboard = (dashboardName: string) => {
    const name = dashboardName.toLowerCase();
    if (name.includes('org') || name.includes('orgs')) return 'Business Metrics';
    if (name.includes('stripe')) return 'Revenue & Billing';
    if (name.includes('sales')) return 'Sales Performance';
    if (name.includes('support')) return 'Customer Success';
    if (name.includes('onboarding')) return 'User Growth';
    if (name.includes('user') || name.includes('customer')) return 'Customer Insights';
    if (name.includes('activity') || name.includes('performance')) return 'Performance';
    if (name.includes('database statistics dashboard')) return 'Development';

    return 'Analytics';
  };

  if (isLoading) return <Loading />;

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <div className="p-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/30 rounded-xl p-6 mt-8"
          >
            <div className="flex items-start">
              <Icon
                icon={Icons.warningTriangle}
                className="w-6 h-6 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5"
              />
              <div className="ml-4">
                <h3 className="text-base font-semibold text-red-800 dark:text-red-200">
                  Unable to load analytics dashboards
                </h3>
                <div className="mt-2 text-sm text-red-700 dark:text-red-300">
                  {error instanceof Error
                    ? error.message
                    : 'An error occurred while fetching dashboard data'}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  const dashboards = data?.dashboards || [];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="p-6">
        {/* Hero Section - ChiroUp Green Theme */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-4 mb-10"
        >
          <div className="flex items-center gap-3">
            <div className="relative">
              <div className="w-12 h-12 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl flex items-center justify-center shadow-md">
                <ChartBarIcon className="w-6 h-6 text-green-600 dark:text-green-400" />
              </div>
              <div className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-white dark:border-gray-900 flex items-center justify-center shadow-sm">
                <Zap className="w-2 h-2 text-white" />
              </div>
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Analytics Hub</h1>
              <p className="text-gray-600 dark:text-gray-400 text-sm">
                Explore comprehensive dashboards and insights
              </p>
            </div>
          </div>
        </motion.div>

        {/* Dashboards Grid */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6"
        >
          {dashboards.map((dashboard, index) => {
            const IconComponent = getIconForDashboard(dashboard.name);
            const category = getCategoryForDashboard(dashboard.name);
            const isLocked = false; // Sales Dashboard is now unlocked

            return (
              <motion.div
                key={dashboard.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                onClick={isLocked ? undefined : () => navigate(`/analytics/${dashboard.id}`)}
                className={`group relative ${isLocked ? 'cursor-not-allowed' : 'cursor-pointer'}`}
              >
                {/* Outer Glow Container */}
                <div className="relative h-full group-hover:drop-shadow-2xl transition-all duration-500">
                  {/* Shimmery Outer Glow Effect - Green Theme */}
                  <div className="absolute -inset-2 opacity-0 group-hover:opacity-100 transition-opacity duration-700 rounded-3xl bg-gradient-to-r from-green-500/60 via-emerald-500/70 to-teal-500/60 blur-xl"></div>

                  {/* ChiroUp Green-Style Card */}
                  <div className="relative h-full bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-700 transition-all duration-500 hover:-translate-y-2 hover:scale-[1.02] overflow-hidden hover:shadow-2xl hover:border-green-200 dark:hover:border-green-800">
                    {/* Coming Soon Badge for locked dashboards with Pulse */}
                    {isLocked && (
                      <div className="absolute top-4 right-4 z-10">
                        <div className="relative">
                          {/* Pulsing ring */}
                          <div className="absolute inset-0 bg-amber-400 rounded-full animate-ping opacity-75"></div>
                          {/* Badge */}
                          <div className="relative bg-gradient-to-r from-amber-500 to-orange-500 text-white text-xs font-bold px-3 py-1.5 rounded-full shadow-lg flex items-center gap-1.5">
                            <Icon icon={Icons.lock} className="w-3 h-3" />
                            <span>Coming Soon</span>
                          </div>
                        </div>
                      </div>
                    )}
                    {/* Content */}
                    <div className="relative p-6 h-full flex flex-col">
                      {/* ChiroUp Green Category Badge */}
                      <div className="mb-4">
                        <span className="text-xs font-semibold text-green-600 dark:text-green-400 uppercase tracking-wider">
                          {category}
                        </span>
                      </div>

                      {/* Premium Icon Section */}
                      <div className="mb-5">
                        <div className="relative w-16 h-16 rounded-2xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 flex items-center justify-center shadow-md transform transition-all duration-300 group-hover:scale-110 group-hover:shadow-xl group-hover:border-green-300 dark:group-hover:border-green-700">
                          <IconComponent className="w-8 h-8 text-green-600 dark:text-green-400" />
                          {/* Subtle green glow on hover */}
                          <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-green-500/10 to-emerald-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                        </div>
                      </div>

                      {/* Enhanced Title Section */}
                      <div className="flex-1">
                        <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2 transition-colors duration-300 group-hover:text-green-600 dark:group-hover:text-green-400">
                          {dashboard.name}
                        </h3>

                        {/* Enhanced description with better line height */}
                        <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed line-clamp-2">
                          {dashboard.description}
                        </p>
                      </div>

                      {/* Enhanced Action Button */}
                      <div className="pt-4 mt-auto">
                        {isLocked ? (
                          <div className="flex items-center gap-2 text-sm font-semibold text-gray-500 dark:text-gray-400 px-4 py-2.5 rounded-lg bg-gray-100 dark:bg-gray-800">
                            <Icon icon={Icons.lock} className="w-4 h-4" />
                            <span>Coming Soon</span>
                          </div>
                        ) : (
                          <div className="flex items-center justify-between px-4 py-2.5 rounded-lg bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border border-green-200 dark:border-green-800 transition-all duration-300 group-hover:shadow-md group-hover:border-green-300 dark:group-hover:border-green-700">
                            <span className="text-sm font-semibold text-green-600 dark:text-green-400">
                              View Dashboard
                            </span>
                            <ArrowRightIcon className="w-4 h-4 text-green-600 dark:text-green-400 transition-all duration-300 group-hover:translate-x-1" />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })}

          {/* Support Dashboard - Now Available */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: dashboards.length * 0.1 }}
            onClick={() => navigate('/analytics/support')}
            className="group relative cursor-pointer"
          >
            {/* Outer Glow Container */}
            <div className="relative h-full group-hover:drop-shadow-2xl transition-all duration-500">
              {/* Shimmery Outer Glow Effect - Green Theme */}
              <div className="absolute -inset-2 opacity-0 group-hover:opacity-100 transition-opacity duration-700 rounded-3xl bg-gradient-to-r from-green-500/60 via-emerald-500/70 to-teal-500/60 blur-xl"></div>

              {/* ChiroUp Green-Style Card */}
              <div className="relative h-full bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-700 transition-all duration-500 hover:-translate-y-2 hover:scale-[1.02] overflow-hidden hover:shadow-2xl hover:border-green-200 dark:hover:border-green-800">
                {/* Content */}
                <div className="relative p-6 h-full flex flex-col">
                  {/* ChiroUp Green Category Badge */}
                  <div className="mb-4">
                    <span className="text-xs font-semibold text-green-600 dark:text-green-400 uppercase tracking-wider">
                      Customer Success
                    </span>
                  </div>

                  {/* Premium Icon Section */}
                  <div className="mb-5">
                    <div className="relative w-16 h-16 rounded-2xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 flex items-center justify-center shadow-md transform transition-all duration-300 group-hover:scale-110 group-hover:shadow-xl group-hover:border-green-300 dark:group-hover:border-green-700">
                      <Headphones className="w-8 h-8 text-green-600 dark:text-green-400" />
                      {/* Subtle green glow on hover */}
                      <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-green-500/10 to-emerald-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    </div>
                  </div>

                  {/* Enhanced Title Section */}
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2 transition-colors duration-300 group-hover:text-green-600 dark:group-hover:text-green-400">
                      Support Dashboard
                    </h3>

                    {/* Enhanced description with better line height */}
                    <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed line-clamp-2">
                      Customer support metrics, ticket analytics, and team performance tracking
                    </p>
                  </div>

                  {/* Enhanced Action Button */}
                  <div className="pt-4 mt-auto">
                    <div className="flex items-center justify-between px-4 py-2.5 rounded-lg bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border border-green-200 dark:border-green-800 transition-all duration-300 group-hover:shadow-md group-hover:border-green-300 dark:group-hover:border-green-700">
                      <span className="text-sm font-semibold text-green-600 dark:text-green-400">
                        View Dashboard
                      </span>
                      <ArrowRightIcon className="w-4 h-4 text-green-600 dark:text-green-400 transition-all duration-300 group-hover:translate-x-1" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Onboarding Dashboard - Now Available */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: (dashboards.length + 1) * 0.1 }}
            onClick={() => navigate('/analytics/onboarding')}
            className="group relative cursor-pointer"
          >
            {/* Outer Glow Container */}
            <div className="relative h-full group-hover:drop-shadow-2xl transition-all duration-500">
              {/* Shimmery Outer Glow Effect - Green Theme */}
              <div className="absolute -inset-2 opacity-0 group-hover:opacity-100 transition-opacity duration-700 rounded-3xl bg-gradient-to-r from-green-500/60 via-emerald-500/70 to-teal-500/60 blur-xl"></div>

              {/* ChiroUp Green-Style Card */}
              <div className="relative h-full bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-700 transition-all duration-500 hover:-translate-y-2 hover:scale-[1.02] overflow-hidden hover:shadow-2xl hover:border-green-200 dark:hover:border-green-800">
                {/* Content */}
                <div className="relative p-6 h-full flex flex-col">
                  {/* ChiroUp Green Category Badge */}
                  <div className="mb-4">
                    <span className="text-xs font-semibold text-green-600 dark:text-green-400 uppercase tracking-wider">
                      User Growth
                    </span>
                  </div>

                  {/* Premium Icon Section */}
                  <div className="mb-5">
                    <div className="relative w-16 h-16 rounded-2xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 flex items-center justify-center shadow-md transform transition-all duration-300 group-hover:scale-110 group-hover:shadow-xl group-hover:border-green-300 dark:group-hover:border-green-700">
                      <UserPlus className="w-8 h-8 text-green-600 dark:text-green-400" />
                      {/* Subtle green glow on hover */}
                      <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-green-500/10 to-emerald-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    </div>
                  </div>

                  {/* Enhanced Title Section */}
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2 transition-colors duration-300 group-hover:text-green-600 dark:group-hover:text-green-400">
                      Onboarding Dashboard
                    </h3>

                    {/* Enhanced description with better line height */}
                    <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed line-clamp-2">
                      User onboarding metrics, conversion tracking, and journey analytics
                    </p>
                  </div>

                  {/* Enhanced Action Button */}
                  <div className="pt-4 mt-auto">
                    <div className="flex items-center justify-between px-4 py-2.5 rounded-lg bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border border-green-200 dark:border-green-800 transition-all duration-300 group-hover:shadow-md group-hover:border-green-300 dark:group-hover:border-green-700">
                      <span className="text-sm font-semibold text-green-600 dark:text-green-400">
                        View Dashboard
                      </span>
                      <ArrowRightIcon className="w-4 h-4 text-green-600 dark:text-green-400 transition-all duration-300 group-hover:translate-x-1" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Subscriber Health Dashboard - Now Available */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: (dashboards.length + 2) * 0.1 }}
            onClick={() => navigate('/analytics/subscriber-demographics')}
            className="group relative cursor-pointer"
          >
            {/* Outer Glow Container */}
            <div className="relative h-full group-hover:drop-shadow-2xl transition-all duration-500">
              {/* Shimmery Outer Glow Effect - Green Theme */}
              <div className="absolute -inset-2 opacity-0 group-hover:opacity-100 transition-opacity duration-700 rounded-3xl bg-gradient-to-r from-green-500/60 via-emerald-500/70 to-teal-500/60 blur-xl"></div>

              {/* ChiroUp Green-Style Card */}
              <div className="relative h-full bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-700 transition-all duration-500 hover:-translate-y-2 hover:scale-[1.02] overflow-hidden hover:shadow-2xl hover:border-green-200 dark:hover:border-green-800">
                {/* Content */}
                <div className="relative p-6 h-full flex flex-col">
                  {/* ChiroUp Green Category Badge */}
                  <div className="mb-4">
                    <span className="text-xs font-semibold text-green-600 dark:text-green-400 uppercase tracking-wider">
                      Customer Insights
                    </span>
                  </div>

                  {/* Premium Icon Section */}
                  <div className="mb-5">
                    <div className="relative w-16 h-16 rounded-2xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 flex items-center justify-center shadow-md transform transition-all duration-300 group-hover:scale-110 group-hover:shadow-xl group-hover:border-green-300 dark:group-hover:border-green-700">
                      <UserCheck className="w-8 h-8 text-green-600 dark:text-green-400" />
                      {/* Subtle green glow on hover */}
                      <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-green-500/10 to-emerald-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    </div>
                  </div>

                  {/* Enhanced Title Section */}
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2 transition-colors duration-300 group-hover:text-green-600 dark:group-hover:text-green-400">
                      Subscriber Health Dashboard
                    </h3>

                    {/* Enhanced description with better line height */}
                    <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed line-clamp-2">
                      Subscriber profiles, demographic insights, and customer segmentation analytics
                    </p>
                  </div>

                  {/* Enhanced Action Button */}
                  <div className="pt-4 mt-auto">
                    <div className="flex items-center justify-between px-4 py-2.5 rounded-lg bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border border-green-200 dark:border-green-800 transition-all duration-300 group-hover:shadow-md group-hover:border-green-300 dark:group-hover:border-green-700">
                      <span className="text-sm font-semibold text-green-600 dark:text-green-400">
                        View Dashboard
                      </span>
                      <ArrowRightIcon className="w-4 h-4 text-green-600 dark:text-green-400 transition-all duration-300 group-hover:translate-x-1" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Billing Readiness Report - Now Available */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: (dashboards.length + 3) * 0.1 }}
            onClick={() => navigate('/analytics/billing-readiness')}
            className="group relative cursor-pointer"
          >
            {/* Outer Glow Container */}
            <div className="relative h-full group-hover:drop-shadow-2xl transition-all duration-500">
              {/* Shimmery Outer Glow Effect - Green Theme */}
              <div className="absolute -inset-2 opacity-0 group-hover:opacity-100 transition-opacity duration-700 rounded-3xl bg-gradient-to-r from-green-500/60 via-emerald-500/70 to-teal-500/60 blur-xl"></div>

              {/* ChiroUp Green-Style Card */}
              <div className="relative h-full bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-700 transition-all duration-500 hover:-translate-y-2 hover:scale-[1.02] overflow-hidden hover:shadow-2xl hover:border-green-200 dark:hover:border-green-800">
                {/* Content */}
                <div className="relative p-6 h-full flex flex-col">
                  {/* ChiroUp Green Category Badge */}
                  <div className="mb-4">
                    <span className="text-xs font-semibold text-green-600 dark:text-green-400 uppercase tracking-wider">
                      Billing
                    </span>
                  </div>

                  {/* Premium Icon Section */}
                  <div className="mb-5">
                    <div className="relative w-16 h-16 rounded-2xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 flex items-center justify-center shadow-md transform transition-all duration-300 group-hover:scale-110 group-hover:shadow-xl group-hover:border-green-300 dark:group-hover:border-green-700">
                      <BanknotesIcon className="w-8 h-8 text-green-600 dark:text-green-400" />
                      {/* Subtle green glow on hover */}
                      <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-green-500/10 to-emerald-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    </div>
                  </div>

                  {/* Enhanced Title Section */}
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2 transition-colors duration-300 group-hover:text-green-600 dark:group-hover:text-green-400">
                      Billing Readiness Report
                    </h3>

                    {/* Enhanced description with better line height */}
                    <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed line-clamp-2">
                      Spot clinics at risk of revenue disruption — claim transmission, rejections,
                      denials, and stalled ERA activity.
                    </p>
                  </div>

                  {/* Enhanced Action Button */}
                  <div className="pt-4 mt-auto">
                    <div className="flex items-center justify-between px-4 py-2.5 rounded-lg bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border border-green-200 dark:border-green-800 transition-all duration-300 group-hover:shadow-md group-hover:border-green-300 dark:group-hover:border-green-700">
                      <span className="text-sm font-semibold text-green-600 dark:text-green-400">
                        View Report
                      </span>
                      <ArrowRightIcon className="w-4 h-4 text-green-600 dark:text-green-400 transition-all duration-300 group-hover:translate-x-1" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Dev Dashboard - Coming Soon (Locked) */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: (dashboards.length + 3) * 0.1 }}
            className="group relative cursor-not-allowed"
          >
            {/* Outer Glow Container */}
            <div className="relative h-full group-hover:drop-shadow-2xl transition-all duration-500">
              {/* Shimmery Outer Glow Effect - Gray for locked */}
              <div className="absolute -inset-2 opacity-0 group-hover:opacity-100 transition-opacity duration-700 rounded-3xl bg-gradient-to-r from-gray-400/40 via-gray-500/50 to-gray-400/40 blur-xl"></div>

              {/* Card */}
              <div className="relative h-full bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-700 transition-all duration-500 overflow-hidden">
                {/* Coming Soon Badge with Pulse */}
                <div className="absolute top-4 right-4 z-10">
                  <div className="relative">
                    {/* Pulsing ring */}
                    <div className="absolute inset-0 bg-amber-400 rounded-full animate-ping opacity-75"></div>
                    {/* Badge */}
                    <div className="relative bg-gradient-to-r from-amber-500 to-orange-500 text-white text-xs font-bold px-3 py-1.5 rounded-full shadow-lg flex items-center gap-1.5">
                      <Icon icon={Icons.lock} className="w-3 h-3" />
                      <span>Coming Soon</span>
                    </div>
                  </div>
                </div>

                {/* Content */}
                <div className="relative p-6 h-full flex flex-col">
                  {/* Category Badge */}
                  <div className="mb-4">
                    <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Development
                    </span>
                  </div>

                  {/* Icon Section */}
                  <div className="mb-5">
                    <div className="relative w-16 h-16 rounded-2xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 flex items-center justify-center shadow-md">
                      <Code className="w-8 h-8 text-gray-400 dark:text-gray-500" />
                    </div>
                  </div>

                  {/* Title Section */}
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                      Dev Dashboard
                    </h3>

                    {/* Description */}
                    <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed line-clamp-2">
                      Development metrics, deployment tracking, and technical performance analytics
                    </p>
                  </div>

                  {/* Locked Button */}
                  <div className="pt-4 mt-auto">
                    <div className="flex items-center gap-2 text-sm font-semibold text-gray-500 dark:text-gray-400 px-4 py-2.5 rounded-lg bg-gray-100 dark:bg-gray-800">
                      <Icon icon={Icons.lock} className="w-4 h-4" />
                      <span>Coming Soon</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Marketing Dashboard - Coming Soon (Locked) */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: (dashboards.length + 4) * 0.1 }}
            className="group relative cursor-not-allowed"
          >
            {/* Outer Glow Container */}
            <div className="relative h-full group-hover:drop-shadow-2xl transition-all duration-500">
              {/* Shimmery Outer Glow Effect - Gray for locked */}
              <div className="absolute -inset-2 opacity-0 group-hover:opacity-100 transition-opacity duration-700 rounded-3xl bg-gradient-to-r from-gray-400/40 via-gray-500/50 to-gray-400/40 blur-xl"></div>

              {/* Card */}
              <div className="relative h-full bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-700 transition-all duration-500 overflow-hidden">
                {/* Coming Soon Badge with Pulse */}
                <div className="absolute top-4 right-4 z-10">
                  <div className="relative">
                    {/* Pulsing ring */}
                    <div className="absolute inset-0 bg-amber-400 rounded-full animate-ping opacity-75"></div>
                    {/* Badge */}
                    <div className="relative bg-gradient-to-r from-amber-500 to-orange-500 text-white text-xs font-bold px-3 py-1.5 rounded-full shadow-lg flex items-center gap-1.5">
                      <Icon icon={Icons.lock} className="w-3 h-3" />
                      <span>Coming Soon</span>
                    </div>
                  </div>
                </div>

                {/* Content */}
                <div className="relative p-6 h-full flex flex-col">
                  {/* Category Badge */}
                  <div className="mb-4">
                    <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                      Marketing & Growth
                    </span>
                  </div>

                  {/* Icon Section */}
                  <div className="mb-5">
                    <div className="relative w-16 h-16 rounded-2xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 flex items-center justify-center shadow-md">
                      <Megaphone className="w-8 h-8 text-gray-400 dark:text-gray-500" />
                    </div>
                  </div>

                  {/* Title Section */}
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
                      Marketing Dashboard
                    </h3>

                    {/* Description */}
                    <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed line-clamp-2">
                      Marketing campaign metrics, conversion analytics, and growth tracking
                    </p>
                  </div>

                  {/* Locked Button */}
                  <div className="pt-4 mt-auto">
                    <div className="flex items-center gap-2 text-sm font-semibold text-gray-500 dark:text-gray-400 px-4 py-2.5 rounded-lg bg-gray-100 dark:bg-gray-800">
                      <Icon icon={Icons.lock} className="w-4 h-4" />
                      <span>Coming Soon</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>

        {/* Empty state */}
        {dashboards.length === 0 && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center py-16"
          >
            <div className="w-20 h-20 mx-auto bg-gray-100 dark:bg-gray-800 rounded-lg flex items-center justify-center mb-6">
              <PresentationChartLineIcon className="w-10 h-10 text-gray-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              No dashboards available
            </h3>
            <p className="text-gray-600 dark:text-gray-400 max-w-sm mx-auto leading-relaxed">
              Analytics dashboards will appear here once they are configured and ready to use.
            </p>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default AnalyticsMain;
